import React, { useMemo, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

// Genuine recorded unique reach per day / per week. Only periods with real events are shown; nothing is estimated.
export function ReachTrend({ adId, trend, pins, since, S }) {
  const T = S.rt;
  const [mode, setMode] = useState("daily");
  const [pin, setPin] = useState("all");
  const data = useMemo(() => {
    const rows = (trend?.[mode] || []).filter((r) => r.pincode === pin);
    return rows.map((r) => {
      const d = mode === "daily" ? r.date : r.week_start;
      return { key: d, label: new Date(d).toLocaleDateString(S.locale, { day: "2-digit", month: "short" }), reach: r.unique_reach };
    });
  }, [trend, mode, pin, S.locale]);
  const tab = (m) => `px-2.5 py-0.5 text-[11px] font-semibold rounded-full ${mode === m ? "bg-emerald-700 text-white" : "text-slate-600"}`;
  return (
    <div className="mb-3 rounded-lg bg-white border border-slate-100 p-2" data-testid={`ad-reach-trend-${adId}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
        <div className="text-[11px] font-semibold text-slate-600">{T.title} · {mode === "daily" ? T.daily : T.weekly}</div>
        <div className="flex items-center gap-1.5">
          <div className="inline-flex rounded-full border border-slate-200 p-0.5" role="tablist">
            <button type="button" className={tab("daily")} onClick={() => setMode("daily")} data-testid={`ad-reach-trend-daily-${adId}`}>{T.daily}</button>
            <button type="button" className={tab("weekly")} onClick={() => setMode("weekly")} data-testid={`ad-reach-trend-weekly-${adId}`}>{T.weekly}</button>
          </div>
          <select value={pin} onChange={(e) => setPin(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-0.5 text-[11px] bg-white" data-testid={`ad-reach-trend-pin-${adId}`}>
            <option value="all">{S.L.allPins}</option>
            {pins.map((p) => <option key={p.pincode} value={p.pincode}>{p.pincode}</option>)}
          </select>
        </div>
      </div>
      <div className="text-[10px] text-slate-500 mb-1" data-testid={`ad-reach-trend-help-${adId}`}>{mode === "daily" ? T.dailyHelp : T.weeklyHelp}</div>
      {data.length === 0 ? <div className="text-[11px] text-slate-400 italic py-4 text-center" data-testid={`ad-reach-trend-empty-${adId}`}>{T.none}</div> : (
        <div className="h-40" data-testid={`ad-reach-trend-chart-${adId}`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ fontSize: 11 }} />
              <Bar dataKey="reach" name={T.title} fill={mode === "daily" ? "#0f766e" : "#065f46"} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      {since && <div className="text-[10px] text-slate-400 mt-1">{T.since} {new Date(since).toLocaleDateString(S.locale)}; {T.before}</div>}
    </div>
  );
}
